import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HookParserEntry, OutletOptions } from 'ngx-dynamic-hooks';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { FractionStripsSum, IViewOnlyQuestion, PuzzleEventService } from 'shared-components';

import { QuestionAnswerType } from 'app/question/constants/question-answer-type';
import { ManipulativesService } from 'app/question/services/manipulatives.service';
import { QuestionTextService } from 'app/question/services/question-text.service';
import { initializeFreeFormAnswersIfNotExist } from 'app/question/utils/questions-utils';
import { IGameQuestion } from 'app/student/models/game-question';
import { ValidationService } from 'app/student/services/validation.service';
import { QuestionEventsService } from '../question/question-events.service';
import { QuestionTextParserService } from '../question/question-text-parser.service';

export type DynamicContextType = {
  answerChanged: (answer: any) => void;
  manipItemIndexChanged: (index: number) => void;
  Question: IViewOnlyQuestion;
  submit: (answer: any) => void;
  submitFreeformPuzzle: (answer: any) => void;
  SumValue: number;
};

@UntilDestroy()
@Component({
    providers: [
        PuzzleEventService
    ],
    selector: 'kh-question-text',
    styleUrls: ['./question-text.component.less'],
    templateUrl: './question-text.component.html',
    standalone: false
})
export class QuestionTextComponent implements OnChanges, OnInit {
  @Input() public question!: IViewOnlyQuestion | Observable<IViewOnlyQuestion>;
  @Input() public textProperty?: string;
  @Input() public hideStudentManipControlBar = false;
  @Input() public enablePuzzleSubmit = false;
  @Input() public isAssessment = false;

  @Output() public submitFreeFormAnswers = new EventEmitter<any>();

  @ViewChild('hooksEl', { read: ViewContainerRef }) public hooksEl!: ViewContainerRef;

  public dynamicContext: Record<string, any> = {};
  public dynamicOptions: OutletOptions = { sanitize: false };
  public dynamicComponents: HookParserEntry[] = [];

  public questionText = '';
  private questionInstance!: IViewOnlyQuestion;
  private readonly questionTextKey = 'QuestionText';
  private isPuzzle = false;

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly questionTextParser: QuestionTextParserService,
    private readonly injector: Injector,
    private readonly manipulativesService: ManipulativesService,
    private readonly questionTextService: QuestionTextService,
    private readonly questionEventsService: QuestionEventsService,
    private readonly puzzleEventService: PuzzleEventService,
    private readonly zone: NgZone) {}

  public get textKey(): string {
    return this.textProperty ? this.textProperty : this.questionTextKey;
  }

  public ngOnInit(): void {
    if (this.isObservable(this.question)) {
      this.question.pipe(
        untilDestroyed(this)
      ).subscribe((question: IViewOnlyQuestion) => {
        this.questionInstance = question;
        this.renderQuestion();
      });
    }

    this.questionTextService.questionTextUpdated()
      .pipe(
        filter(question => this.question === question),
        tap(() => {
          this.renderQuestion();
        }),
        untilDestroyed(this))
      .subscribe();
  }

  public ngOnChanges(): void {
    if (!this.isObservable(this.question) && this.question) {
      initializeFreeFormAnswersIfNotExist(this.question);
      this.emptyFreeFormAnswersInOpenQuestion(this.question);
      this.questionInstance = this.question;
      this.renderQuestion();
    }
  }

  private isObservable(question: IViewOnlyQuestion | Observable<IViewOnlyQuestion>): question is Observable<IViewOnlyQuestion> {
    return this.question instanceof Observable;
  }

  private renderQuestion(): void {
    this.zone.run(() => {
      this.dynamicComponents = this.manipulativesService.createDynamicHooks(this.injector);
      this.dynamicContext = this.initDynamicComponentsContext();
      this.questionText = this.getQuestionText();
    });

    this.changeDetectorRef.detectChanges();
  }

  private getQuestionText(): string {
    const key = this.textKey;
    const text = this.getTextValue(key);

    if (!text) {
      return '';
    }

    let newText = this.questionTextParser.parseText(text);
    newText = newText.replace(/(Question\.)/g, 'context.Question.');
    newText = newText.replace(/(manipItemIndexChanged\()/g, 'context.manipItemIndexChanged(');
    newText = newText.replace(/(answerChanged\()/g, 'context.answerChanged(');
    newText = newText.replace(/(submit\()/g, 'context.submit(');
    newText = newText.replace(/(SumValue)/g, 'context.SumValue');
    // if this is a puzzle, we add the overlay
    newText = this.injectPuzzleParams(newText);

    return newText;
  }

  private injectPuzzleParams(questionText: string): string {
    const stringToAdd = ' [submitFreeformAnswer]="context.submitFreeformPuzzle"';

    // find any tag that starts with "<kh-puzzle"
    const regex = /<kh-puzzle[-\w]*\b/i; // \b is a word boundary, [-\w]* matches zero or more word characters and hyphens
    const match = questionText.match(regex);

    if (match) {
      const word = match[0]; // Get the matched word
      const position = match.index || 0; // Get the position of the match in the string
      const length = word.length; // Get the length of the matched word
      const insertPosition = position + length;

      questionText = questionText.substring(0, insertPosition) + stringToAdd + questionText.substring(insertPosition);

      return `<kh-puzzle-overlay [disablePuzzleSubmit]="${!this.enablePuzzleSubmit}">${questionText}</kh-puzzle-overlay>`;
    }

    return questionText;
  }

  private initDynamicComponentsContext(): Record<string, any> {
    const sumValue: FractionStripsSum = { Denominator: 2, Numerator: 1 };

    // why is this done twice???
    this.initializeManipIfNotExist();

    // this.initializeManipIfNotExist();

    return {
      answerChanged: (answer: any) => { this.applyChanges(() => { this.freeFormAnswerChanged(answer); }); },
      manipItemIndexChanged: (index: number) => { this.applyChanges(() => { this.manipulativeItemIndexChanged(index); }); },
      Question: this.questionInstance,
      submit: (answer: any) => { this.applyChanges(() => { this.onSubmitFreeFormAnswers([String(answer)]); }); },
      submitFreeformPuzzle: (answer: any) => { this.applyChanges(() => { this.onSubmitFreeFormAnswers([answer]); }); },
      SumValue: sumValue
    };
  }

  private applyChanges(callback: () => void): void {
    callback();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  private manipulativeItemIndexChanged(index: number): void {
    if (this.questionInstance.Manip) {
      this.questionInstance.Manip.ItemIndex = index;
    } else {
      this.questionInstance.Manip = {
        ItemIndex: 0
      };
    }
  }

  private freeFormAnswerChanged(answer: string): void {
    if (!this.questionInstance.FreeFormAnswers) {
      this.questionInstance.FreeFormAnswers = [];
    }
    this.questionInstance.FreeFormAnswers[0] = answer;

    this.changeDetectorRef.detectChanges();
  }

  private onSubmitFreeFormAnswers(answers: string[] = []): void {
    this.questionInstance.ShowFormatError = false;
    const gameQuestion = this.questionInstance as unknown as IGameQuestion;
    if (gameQuestion && !ValidationService.isValidAnswer(answers[0], gameQuestion.AnswerDataType, gameQuestion.LocaleId, gameQuestion.NumberFormatLocale)) {
      gameQuestion.ShowFormatError = true;
    } else {
      this.submitFreeFormAnswers.next(answers);
    }
  }

  private getTextValue(key: string): string {
    return this.questionInstance ? (this.questionInstance as Record<string, any>)[key] : '';
  }

  private initializeManipIfNotExist(): void {
    const questionManipItemIndexRow = 'Question.Manip.ItemIndex';
    const text = this.getTextValue(this.textKey);

    if (text?.includes(questionManipItemIndexRow) && !this.questionInstance.Manip) {
      this.questionInstance.Manip = {
        ItemIndex: 0
      };
    }
  }

  private emptyFreeFormAnswersInOpenQuestion(question: any): void {
    if (!this.isAssessment && question && question.AnswerType && question.AnswerType === QuestionAnswerType.OpenAnswer) {
      question.FreeFormAnswers = [''];
    }
  }
}
